
import { supabase } from '@/integrations/supabase/client';

export interface CertificateAuditStats {
  totalAttempts: number;
  successfulCertificates: number;
  failedAttempts: number;
  errorAttempts: number;
  successRate: number;
  avgModulesCompleted: number;
  avgQuizScore: number;
  periodStart: string;
  periodEnd: string;
}

export interface CertificateAuditEntry {
  id: string;
  userId: string;
  courseId: string;
  attemptTimestamp: string;
  eligibilityStatus: 'eligible' | 'not_eligible' | 'error';
  validationResults: any;
  certificateIssued: boolean;
  certificateId?: string;
  rejectionReason?: string;
  validationDetails: any;
  completedModules: number;
  totalModules: number;
  quizAverage?: number;
  createdAt: string;
}

export class CertificateAuditService {
  // Get certificate audit statistics
  static async getCertificateAuditStats(
    courseId?: string,
    daysBack: number = 30
  ): Promise<CertificateAuditStats | null> {
    try {
      const { data, error } = await supabase.rpc('get_certificate_audit_stats', {
        p_course_id: courseId || null,
        p_days_back: daysBack
      });

      if (error) {
        console.error('Error fetching certificate audit stats:', error);
        return null;
      }

      // Type cast the response as it comes from a database function
      const statsData = data as any;

      if (statsData?.error) {
        console.error('Authorization error:', statsData.error);
        return null;
      }

      return {
        totalAttempts: statsData.total_attempts || 0,
        successfulCertificates: statsData.successful_certificates || 0,
        failedAttempts: statsData.failed_attempts || 0,
        errorAttempts: statsData.error_attempts || 0,
        successRate: statsData.success_rate || 0,
        avgModulesCompleted: statsData.avg_modules_completed || 0,
        avgQuizScore: statsData.avg_quiz_score || 0,
        periodStart: statsData.period_start,
        periodEnd: statsData.period_end
      };
    } catch (error) {
      console.error('Error in getCertificateAuditStats:', error);
      return null;
    }
  }

  // Get detailed certificate audit entries
  static async getCertificateAuditEntries(
    filters?: {
      userId?: string;
      courseId?: string;
      status?: 'eligible' | 'not_eligible' | 'error';
      daysBack?: number;
      limit?: number;
    }
  ): Promise<CertificateAuditEntry[]> {
    try {
      let query = supabase
        .from('certificate_audit_log')
        .select('*')
        .order('attempt_timestamp', { ascending: false });

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters?.courseId) {
        query = query.eq('course_id', filters.courseId);
      }
      if (filters?.status) {
        query = query.eq('eligibility_status', filters.status);
      }
      if (filters?.daysBack) {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - filters.daysBack);
        query = query.gte('attempt_timestamp', cutoffDate.toISOString());
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      } else {
        query = query.limit(100); // Default limit
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching certificate audit entries:', error);
        return [];
      }

      return data?.map(entry => ({
        id: entry.id,
        userId: entry.user_id,
        courseId: entry.course_id,
        attemptTimestamp: entry.attempt_timestamp,
        eligibilityStatus: entry.eligibility_status as 'eligible' | 'not_eligible' | 'error',
        validationResults: entry.validation_results,
        certificateIssued: entry.certificate_issued,
        certificateId: entry.certificate_id,
        rejectionReason: entry.rejection_reason,
        validationDetails: entry.validation_details,
        completedModules: entry.completed_modules,
        totalModules: entry.total_modules,
        quizAverage: entry.quiz_average,
        createdAt: entry.created_at
      })) || [];
    } catch (error) {
      console.error('Error in getCertificateAuditEntries:', error);
      return [];
    }
  }

  // Get certificate validation trends
  static async getCertificateValidationTrends(
    courseId?: string,
    daysBack: number = 30
  ): Promise<{
    dailyStats: Array<{
      date: string;
      attempts: number;
      successes: number;
      failures: number;
      successRate: number;
    }>;
    topFailureReasons: Array<{
      reason: string;
      count: number;
      percentage: number;
    }>;
  }> {
    try {
      const { data: entries } = await supabase
        .from('certificate_audit_log')
        .select('attempt_timestamp, eligibility_status, rejection_reason')
        .gte('attempt_timestamp', new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString())
        .eq('course_id', courseId || '')
        .order('attempt_timestamp', { ascending: true });

      if (!entries) return { dailyStats: [], topFailureReasons: [] };

      // Group by date
      const dailyGroups: { [date: string]: any[] } = {};
      entries.forEach(entry => {
        const date = entry.attempt_timestamp.split('T')[0];
        if (!dailyGroups[date]) dailyGroups[date] = [];
        dailyGroups[date].push(entry);
      });

      // Calculate daily stats
      const dailyStats = Object.entries(dailyGroups).map(([date, dayEntries]) => {
        const attempts = dayEntries.length;
        const successes = dayEntries.filter(e => e.eligibility_status === 'eligible').length;
        const failures = attempts - successes;
        const successRate = attempts > 0 ? (successes / attempts) * 100 : 0;

        return { date, attempts, successes, failures, successRate };
      });

      // Calculate top failure reasons
      const failureReasons: { [reason: string]: number } = {};
      entries
        .filter(e => e.eligibility_status !== 'eligible' && e.rejection_reason)
        .forEach(entry => {
          const reason = entry.rejection_reason || 'Unknown';
          failureReasons[reason] = (failureReasons[reason] || 0) + 1;
        });

      const totalFailures = Object.values(failureReasons).reduce((sum, count) => sum + count, 0);
      const topFailureReasons = Object.entries(failureReasons)
        .map(([reason, count]) => ({
          reason,
          count,
          percentage: totalFailures > 0 ? (count / totalFailures) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      return { dailyStats, topFailureReasons };
    } catch (error) {
      console.error('Error in getCertificateValidationTrends:', error);
      return { dailyStats: [], topFailureReasons: [] };
    }
  }

  // Update audit entry when certificate is issued
  static async updateAuditEntryWithCertificate(
    auditId: string,
    certificateId: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('certificate_audit_log')
        .update({
          certificate_issued: true,
          certificate_id: certificateId
        })
        .eq('id', auditId);

      if (error) {
        console.error('Error updating audit entry with certificate:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateAuditEntryWithCertificate:', error);
      return false;
    }
  }

  // Get user's certificate validation history
  static async getUserCertificateHistory(
    userId: string,
    courseId?: string
  ): Promise<CertificateAuditEntry[]> {
    try {
      let query = supabase
        .from('certificate_audit_log')
        .select('*')
        .eq('user_id', userId)
        .order('attempt_timestamp', { ascending: false });

      if (courseId) {
        query = query.eq('course_id', courseId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching user certificate history:', error);
        return [];
      }

      return data?.map(entry => ({
        id: entry.id,
        userId: entry.user_id,
        courseId: entry.course_id,
        attemptTimestamp: entry.attempt_timestamp,
        eligibilityStatus: entry.eligibility_status as 'eligible' | 'not_eligible' | 'error',
        validationResults: entry.validation_results,
        certificateIssued: entry.certificate_issued,
        certificateId: entry.certificate_id,
        rejectionReason: entry.rejection_reason,
        validationDetails: entry.validation_details,
        completedModules: entry.completed_modules,
        totalModules: entry.total_modules,
        quizAverage: entry.quiz_average,
        createdAt: entry.created_at
      })) || [];
    } catch (error) {
      console.error('Error in getUserCertificateHistory:', error);
      return [];
    }
  }
}
